import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { styles } from './../../../styles/themes/style';
import styled from 'styled-components'
import Column from '../../../components/Column';
import Row from '../../../components/Row';
import ApiKey from '../components/integrations/ApiKey';
import Hubspot from '../components/integrations/hubspot/Hubspot';
import HubspotLegacy from '../components/integrations/hubspot/HubspotLegacy';
import ApiDocs from '../components/integrations/ApiDocs';
import Zapier from '../components/integrations/Zapier';
import McpServerDocs from '../components/integrations/McpServerDocs';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import { useNavigate, useParams } from 'react-router-dom';
import { AppRoutes } from '../../../interfaces/store/appStore';

interface IIntegrationsSectionProps { }

const IntegrationsSection: FunctionComponent<IIntegrationsSectionProps> = () => {
    const { integration } = useParams<{ integration?: string }>()
    const [selectedPage, setSelectedPage] = useState<string>('apiKey')
    const { store: { workspace, workspaceOwner, workspaceAdmin } } = useAuthStore()
    const navigate = useNavigate()

    const hubspotEnabled = workspace?.hubspotEnabled === true
    const isAdmin = workspaceOwner || workspaceAdmin
    // Every member can hold a personal API key, so the key page and the docs for using it are open to all.
    // GPTs and Pabbly Connect are gone for everyone (Pabbly is switched off in the backend too);
    // their old URLs fall through to the redirect below.
    const sections = useMemo(() => ([
        {id: 'apiKey', label: 'API Key', enabled: true},
        {id: 'api', label: 'API', enabled: true},
        {id: 'mcpServer', label: 'MCP Server', enabled: true},
        {id: 'zapier', label: 'Zapier', enabled: isAdmin},
        {id: 'hubspotLegacy', label: 'Hubspot Legacy', enabled: false},
        {id: 'hubspot', label: 'Hubspot', enabled: hubspotEnabled}
    ]), [hubspotEnabled, isAdmin])


    // Set selected page based on URL parameter
    useEffect(() => {
        if (integration && sections.some(s => s.id === integration)) {
            setSelectedPage(integration)
        } else if (integration) {
            // If integration param exists but is invalid, redirect to base integrations
            navigate(AppRoutes.INTEGRATIONS)
        }
    }, [integration])

    const renderRoute = () => {
        switch (selectedPage) {
            case 'zapier':
                return <Zapier />
            case 'api':
                return <ApiDocs />
            case 'mcpServer':
                return <McpServerDocs />
            case 'hubspot':
                return hubspotEnabled ? <Hubspot /> : <ApiKey />
            case 'hubspotLegacy':
                return <HubspotLegacy />
            case 'apiKey':
            default:
                return <ApiKey />
        }
    }

    useEffect(() => {
        if(workspace?.isStarter) navigate(AppRoutes.HOME)
    }, [workspace])

    return (
        <Container>
            <StyledRow gap='10px'>
                {sections.map((integration) => integration.enabled && (
                    <NavButton key={integration.id} $disabled={false} $selected={selectedPage === integration.id}>
                        <a onClick={() => navigate(`${AppRoutes.INTEGRATIONS}/${integration.id}`)}>{integration.label}</a>
                    </NavButton>
                ))}
            </StyledRow>
            <ScrollContent>
                {renderRoute()}
            </ScrollContent>
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0px 16px 0px 16px;
`
const ScrollContent = styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    padding-bottom: 32px;
`
const StyledRow = styled(Row)`
  border-bottom: 1px solid ${({ theme: { quinaryColor} }) => quinaryColor};
  flex-shrink: 0;
`
const NavButton = styled.div<{$disabled?: boolean, $selected?: boolean}>`
    line-height: 20px;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: bold;
    padding: 0px 12px;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 12px;
    a {
      ${({ theme: { primaryColor } }) => primaryColor && `color: ${primaryColor};`}
    }
    ${({ $disabled }) => $disabled && `
        cursor: default;
        a, &:hover a, &:focus a{
            cursor: default;
            color: ${styles.colors.disabledState} !important;
        }
    `}
    ${({ $selected }) => $selected && `
        border-bottom: 2px solid #5964FF;
        cursor: default;
        a {
            cursor: default;
            color: ${styles.colors.primary600} !important;
        }
    `}
`
export default IntegrationsSection
